import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const amount = (value) => Number(value || 0).toLocaleString("en-US", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const statusLabel = (value) => String(value || "Unknown").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

/** A review owns its invoice snapshot; a failed or dismissed review cannot be reused. */
export function useProjectClosureConfirmation(contextKey) {
  const pending = useRef(null);
  const submitting = useRef(false);
  const [preview, setPreview] = useState(null);
  const [closureBusy, setClosureBusy] = useState(false);

  useEffect(() => {
    pending.current = null;
    setPreview(null);
    setClosureBusy(false);
    return () => { pending.current = null; };
  }, [contextKey]);

  const clear = () => {
    pending.current = null;
    setPreview(null);
    setClosureBusy(false);
  };

  const reportError = async (error, request) => {
    const detail = error?.response?.data?.detail;
    toast.error(typeof detail === "string" ? detail : detail?.message || error?.message || "Invoice review could not be completed. Review the project again.");
    try {
      await request.onError?.(error);
    } catch {
      toast.error("The project could not be refreshed. Reload it before trying again.");
    }
  };

  const requestClosure = async (request) => {
    if (pending.current || submitting.current) return;
    pending.current = request;
    setClosureBusy(true);
    try {
      const snapshot = await request.loadPreview();
      if (pending.current !== request) return;
      if (!snapshot || !Array.isArray(snapshot.invoices)) {
        throw new Error("Invoice review is unavailable. Review the project again.");
      }
      request.snapshot = snapshot;
      setPreview(snapshot);
      setClosureBusy(false);
    } catch (error) {
      if (pending.current !== request) return;
      await reportError(error, request);
      if (pending.current === request) clear();
    }
  };

  const cancel = () => {
    if (!submitting.current) clear();
  };

  const close = async () => {
    const request = pending.current;
    if (!request?.snapshot || submitting.current) return;
    submitting.current = true;
    setClosureBusy(true);
    try {
      await request.close(request.snapshot);
    } catch (error) {
      await reportError(error, request);
    } finally {
      submitting.current = false;
      if (pending.current === request) clear();
    }
  };

  const closureDialog = (
    <AlertDialog open={Boolean(preview)} onOpenChange={(open) => { if (!open) cancel(); }}>
      <AlertDialogContent data-testid="project-closure-confirmation" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to close this project?</AlertDialogTitle>
          <AlertDialogDescription>
            Review invoice payments before closing. Close records your manual override; invoice balances remain unchanged.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {preview && <>
          {preview.invoice_count === 0 ? (
            <p role="alert">No invoices are attached to this project. You can still choose Close to confirm the override.</p>
          ) : preview.applicable_invoice_count === 0 ? (
            <p role="alert">No applicable invoices are attached to this project. You can still choose Close to confirm the override.</p>
          ) : !preview.all_paid ? (
            <p role="alert">This project has unpaid or partially paid invoices. Closing does not settle these balances. You can still choose Close to confirm the override.</p>
          ) : null}
          {preview.invoices.length > 0 && <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="Invoice payment review">
              <thead><tr>
                <th scope="col" className="text-left p-2">Invoice number</th>
                <th scope="col" className="text-left p-2">Payment status</th>
                <th scope="col" className="text-right p-2">Invoice amount</th>
                <th scope="col" className="text-right p-2">Paid amount</th>
                <th scope="col" className="text-right p-2">Remaining balance</th>
              </tr></thead>
              <tbody>{preview.invoices.map((invoice) => <tr key={invoice.id}>
                <th scope="row" className="text-left p-2">{invoice.invoice_number || "Unnumbered invoice"}{invoice.applicable === false && <span className="block font-normal">Not applicable{invoice.exclusion_reason ? `: ${invoice.exclusion_reason}` : ""}</span>}</th>
                <td className="p-2">{invoice.legacy_paid ? "Marked Paid (legacy record)" : statusLabel(invoice.payment_status)}</td>
                <td className="text-right p-2">{amount(invoice.invoice_amount)}</td>
                <td className="text-right p-2">{amount(invoice.paid_amount)}</td>
                <td className="text-right p-2">{amount(invoice.balance)}</td>
              </tr>)}</tbody>
            </table>
          </div>}
          <p>Outstanding balance: {amount(preview.outstanding_balance)}</p>
        </>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={closureBusy} onClick={cancel}>Cancel</AlertDialogCancel>
          <Button disabled={closureBusy} onClick={close} type="button">{closureBusy ? "Closing…" : "Close"}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { requestClosure, closureDialog, closureBusy };
}
